/* =====================================================================
 * createTheme — produces an AppTheme for a given mode + optional
 * tenant palette overrides.
 *
 * Usage:
 *   const light = createTheme('light');
 *   const dark  = createTheme('dark');
 *   const b2b   = createTheme('light', {
 *                   light: { accent: { base: '#0052cc' } },
 *                   dark:  { accent: { base: '#4c9aff' } },
 *                 });
 *
 * The returned object is structurally identical to the legacy
 * lightTheme / darkTheme consts, which means no downstream component
 * has to know we now have a factory.
 * ===================================================================== */

import {
  basePalettes,
  type Palette,
  type ThemeMode,
} from '../tokens/palette';
import {
  sizes,
  shadows,
  breakpoints,
  transitions,
} from '../tokens/dimensions';
import { fonts } from '../tokens/typography';
import { phaseColors } from '../tokens/accent';
import {
  markdownTokens,
  markdownMobileTokens,
  bubbleTokens,
  bubbleMobileTokens,
  modalTokens,
  modalMobileTokens,
  inputTokens,
  inputMobileTokens,
  dmTokens,
  dmMobileTokens,
  sidebarTokens,
  sidebarMobileTokens,
  authTokens,
  authMobileTokens,
} from '../tokens/component';
import { resolveColors } from './colors';
import type {
  AppTheme,
  PaletteOverride,
  TenantThemeOverride,
} from './types';

function mergePalette(base: Palette, override?: PaletteOverride): Palette {
  if (!override) return base;
  return {
    surface: { ...base.surface, ...(override.surface ?? {}) },
    text: { ...base.text, ...(override.text ?? {}) },
    border: { ...base.border, ...(override.border ?? {}) },
    accent: { ...base.accent, ...(override.accent ?? {}) },
    brand: { ...base.brand, ...(override.brand ?? {}) },
    feedback: { ...base.feedback, ...(override.feedback ?? {}) },
    usage: { ...base.usage, ...(override.usage ?? {}) },
    scrollbar: { ...base.scrollbar, ...(override.scrollbar ?? {}) },
  };
}

/**
 * Build an AppTheme for the given mode, optionally overriding palette
 * slots with tenant-specific values.
 */
export function createTheme(
  mode: ThemeMode,
  overrides?: TenantThemeOverride,
): AppTheme {
  const palette = mergePalette(basePalettes[mode], overrides?.[mode]);
  return {
    mode,
    colors: resolveColors(palette),
    fonts,
    sizes,
    shadows,
    breakpoints,
    transitions,
    phaseColors,
    markdown: markdownTokens,
    markdownMobile: markdownMobileTokens,
    bubble: bubbleTokens,
    bubbleMobile: bubbleMobileTokens,
    modal: modalTokens,
    modalMobile: modalMobileTokens,
    input: inputTokens,
    inputMobile: inputMobileTokens,
    dm: dmTokens,
    dmMobile: dmMobileTokens,
    sidebar: sidebarTokens,
    sidebarMobile: sidebarMobileTokens,
    auth: authTokens,
    authMobile: authMobileTokens,
  };
}
